import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { renderRoutes } from 'react-router-config';
import { ArrowLeft } from 'lucide-react';
import {
  PostAJobFlowProvider,
  usePostAJobFlow,
} from './flow/PostAJobFlowContext';
import ResumeProgressModal from './ResumeProgressModal';
import styles from './PostAJobFlowShell.module.scss';

const EXIT_FALLBACK_MS = 520;

const PostAJobFlowChrome = ({ route }) => {
  const {
    progress,
    canGoBack,
    goBack,
    offerResume,
    flowReady,
    resumeProgress,
    startAgain,
    registerPrepareExit,
    completeExitTransition,
  } = usePostAJobFlow();
  const stepSlotRef = useRef(null);
  const [exitOverlay, setExitOverlay] = useState(null);

  const prepareExit = useCallback((direction) => {
    const slot = stepSlotRef.current;
    setExitOverlay({
      direction,
      html: slot ? slot.innerHTML : '',
      height: slot ? slot.offsetHeight : undefined,
    });
  }, []);

  useEffect(() => registerPrepareExit(prepareExit), [
    registerPrepareExit,
    prepareExit,
  ]);

  const finishExit = useCallback(() => {
    setExitOverlay(null);
    completeExitTransition();
  }, [completeExitTransition]);

  useEffect(() => {
    if (!exitOverlay) {
      return undefined;
    }

    const timeoutId = window.setTimeout(finishExit, EXIT_FALLBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [exitOverlay, finishExit]);

  const exitOverlayClassName = [
    styles.exitOverlay,
    exitOverlay && exitOverlay.direction === 'forward'
      ? styles.exitForward
      : null,
    exitOverlay && exitOverlay.direction === 'back' ? styles.exitBack : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.shell}>
      <div className={styles.progressTrack} aria-hidden="true">
        <div
          className={styles.progressFill}
          style={{
            width: `${Math.max(flowReady ? progress : 0, 0.02) * 100}%`,
          }}
        />
      </div>

      {flowReady && canGoBack ? (
        <button
          type="button"
          className={styles.backButton}
          onClick={goBack}
          aria-label="Go back"
          disabled={Boolean(exitOverlay) || offerResume}
        >
          <ArrowLeft size={22} strokeWidth={2} aria-hidden="true" />
        </button>
      ) : null}

      <div className={styles.stepStage}>
        <div className={styles.stepSlot} ref={stepSlotRef}>
          {flowReady ? renderRoutes(route.routes) : null}
        </div>
        {exitOverlay ? (
          <div
            className={exitOverlayClassName}
            style={
              exitOverlay.height ? { minHeight: exitOverlay.height } : undefined
            }
            // Visual snapshot of the outgoing step only.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: exitOverlay.html }}
            aria-hidden="true"
            onAnimationEnd={(event) => {
              if (event.target !== event.currentTarget) {
                return;
              }
              finishExit();
            }}
          />
        ) : null}
      </div>

      <ResumeProgressModal
        show={flowReady && offerResume}
        onStartAgain={startAgain}
        onContinue={resumeProgress}
      />
    </div>
  );
};

PostAJobFlowChrome.propTypes = {
  route: PropTypes.shape({
    routes: PropTypes.array,
  }).isRequired,
};

const PostAJobFlowShell = ({ route }) => (
  <PostAJobFlowProvider>
    <PostAJobFlowChrome route={route} />
  </PostAJobFlowProvider>
);

PostAJobFlowShell.propTypes = {
  route: PropTypes.shape({
    routes: PropTypes.array,
  }).isRequired,
};

export default PostAJobFlowShell;
